import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  NoSymbolIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import api from '@/services/api'

interface Employee {
  id: number
  firstName: string
  lastName: string
  employeeNumber: string
  department: string
  position: string
}

interface LeaveType {
  id: number
  name: string
  code: string
  isPaid: boolean
}

interface Approver {
  id: number
  firstName: string
  lastName: string
}

interface LeaveRequest {
  id: number
  startDate: string
  endDate: string
  daysRequested: number
  reason: string | null
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  createdAt: string
  approvedAt: string | null
  rejectedAt: string | null
  rejectionReason: string | null
  employee: Employee
  leaveType: LeaveType
  approver: Approver | null
  rejecter: Approver | null
}

interface CalendarLeaveRequest extends LeaveRequest {
  startDateObj: Date
  endDateObj: Date
}

interface LeaveDashboardProps {
  onRefresh?: () => void
}

const LeaveDashboard: React.FC<LeaveDashboardProps> = () => {
  const { user } = useAuth()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedRequest, setSelectedRequest] = useState<CalendarLeaveRequest | null>(null)
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')

  // Get leave requests for calendar view
  const { data: requestsData, isLoading, refetch } = useQuery({
    queryKey: ['leaveRequests', 'calendar', currentDate.getFullYear(), currentDate.getMonth()],
    queryFn: async () => {
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const response = await api.get(`/leaves/requests?year=${year}&month=${month}`)
      
      // Transform dates to Date objects for easier manipulation
      const requests = response.data.data.leaveRequests.map((request: LeaveRequest): CalendarLeaveRequest => ({
        ...request,
        startDateObj: new Date(request.startDate),
        endDateObj: new Date(request.endDate)
      }))
      
      return { leaveRequests: requests }
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  })

  // Calendar navigation
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev)
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1)
      } else {
        newDate.setMonth(prev.getMonth() + 1)
      }
      return newDate
    })
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    
    const firstDay = new Date(year, month, 1)
    const calendarStart = new Date(firstDay)
    calendarStart.setDate(firstDay.getDate() - firstDay.getDay())
    
    const days = []
    const currentDay = new Date(calendarStart)
    
    // Generate 42 days (6 weeks) for calendar grid
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDay))
      currentDay.setDate(currentDay.getDate() + 1)
    }
    
    return days
  }

  // Get leave requests for a specific day
  const getLeaveRequestsForDay = (day: Date): CalendarLeaveRequest[] => {
    if (!requestsData?.leaveRequests) return []
    
    return requestsData.leaveRequests.filter((request: CalendarLeaveRequest) => {
      const dayTime = day.getTime()
      const startTime = request.startDateObj.getTime()
      const endTime = request.endDateObj.getTime()
      
      return dayTime >= startTime && dayTime <= endTime
    })
  }

  const isCurrentMonth = (day: Date) => {
    return day.getMonth() === currentDate.getMonth()
  }

  const isToday = (day: Date) => {
    const today = new Date()
    return day.toDateString() === today.toDateString()
  }

  const getStatusBgColor = (status: LeaveRequest['status']) => {
    const colors = {
      pending: 'bg-yellow-50 border-yellow-200',
      approved: 'bg-green-50 border-green-200',
      rejected: 'bg-red-50 border-red-200',
      cancelled: 'bg-gray-50 border-gray-200'
    }
    return colors[status]
  }

  const getStatusTextColor = (status: LeaveRequest['status']) => {
    const colors = {
      pending: 'text-yellow-800',
      approved: 'text-green-800',
      rejected: 'text-red-800',
      cancelled: 'text-gray-800'
    }
    return colors[status]
  }

  const getStatusIcon = (status: LeaveRequest['status']) => {
    const icons = {
      pending: ClockIcon,
      approved: CheckCircleIcon,
      rejected: XCircleIcon,
      cancelled: NoSymbolIcon
    }
    return icons[status]
  }

  // Statistics
  const getStats = () => {
    if (!requestsData?.leaveRequests) return []

    const requests = requestsData.leaveRequests
    const total = requests.length
    const pending = requests.filter((r: { status: string }) => r.status === 'pending').length
    const approved = requests.filter((r: { status: string }) => r.status === 'approved').length
    const rejected = requests.filter((r: { status: string }) => r.status === 'rejected').length

    return [
      { label: 'Total Requests', value: total, color: 'bg-blue-500' },
      { label: 'Pending', value: pending, color: 'bg-yellow-500' },
      { label: 'Approved', value: approved, color: 'bg-green-500' },
      { label: 'Rejected', value: rejected, color: 'bg-red-500' }
    ]
  }

  const calendarDays = generateCalendarDays()
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const stats = getStats()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          <p className="mt-2 text-sm font-medium text-gray-900">Loading calendar...</p>
          <p className="text-xs text-gray-500">Fetching leave requests</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Leave Calendar</h1>
            <p className="text-gray-600 text-sm mt-0.5">
              Manage and track employee leave requests
            </p>
          </div>
          
          <div className="flex items-center space-x-2 mt-3 lg:mt-0">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                viewMode === 'list' 
                  ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                  : 'text-gray-600 hover:text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              List View
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                viewMode === 'calendar' 
                  ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                  : 'text-gray-600 hover:text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Calendar View
            </button>
            <button
              onClick={() => refetch()}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {stats.map((stat, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-600">{stat.label}</p>
                  <p className="text-lg font-bold text-gray-900 mt-0.5">{stat.value}</p>
                </div>
                <div className={`h-8 w-8 rounded-lg ${stat.color} flex items-center justify-center`}>
                  <CalendarIcon className="h-4 w-4 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <CalendarIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <p className="text-gray-600 text-sm">Leave schedule overview</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 mt-3 lg:mt-0">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            
            <button
              onClick={goToToday}
              className="px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Today
            </button>
            
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 text-gray-400 hover:text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-xs text-gray-600">Pending</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-xs text-gray-600">Approved</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span className="text-xs text-gray-600">Rejected</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span className="text-xs text-gray-600">Cancelled</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
            {dayNames.map(day => (
              <div key={day} className="px-2 py-3 text-center text-xs font-semibold text-gray-700">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {calendarDays.map((day, index) => {
              const dayRequests = getLeaveRequestsForDay(day)
              const isCurrentMonthDay = isCurrentMonth(day)
              const isTodayDay = isToday(day)

              return (
                <div
                  key={index}
                  className={`min-h-[120px] p-2 border-b border-r border-gray-200 transition-colors ${
                    isCurrentMonthDay ? 'bg-white' : 'bg-gray-50'
                  } ${isTodayDay ? 'bg-blue-50 border-blue-200' : ''} ${
                    dayRequests.length > 0 ? 'hover:bg-gray-50' : ''
                  }`}
                >
                  <div className={`flex items-center justify-between mb-1 ${
                    isCurrentMonthDay ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    <span className={`text-xs font-medium ${
                      isTodayDay ? 'bg-blue-600 text-white px-1.5 py-0.5 rounded-full' : ''
                    }`}>
                      {day.getDate()}
                    </span>
                    {dayRequests.length > 0 && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                        {dayRequests.length}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1">
                    {dayRequests.slice(0, 3).map((request) => {
                      const StatusIcon = getStatusIcon(request.status)
                      return (
                        <div
                          key={request.id}
                          onClick={() => setSelectedRequest(request)}
                          className={`p-1.5 rounded border cursor-pointer transition-all hover:shadow-sm hover:scale-105 ${getStatusBgColor(request.status)}`}
                          title={`${request.employee.firstName} ${request.employee.lastName} - ${request.leaveType.name}`}
                        >
                          <div className="flex items-center space-x-1">
                            <StatusIcon className={`h-2.5 w-2.5 ${getStatusTextColor(request.status)}`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-xs font-medium truncate ${getStatusTextColor(request.status)}`}>
                                {user?.role === 'employee' ? request.leaveType.code : `${request.employee.firstName}`}
                              </p>
                              <p className="text-xs text-gray-500 truncate">
                                {request.leaveType.name}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    
                    {dayRequests.length > 3 && (
                      <div 
                        className="text-xs text-gray-500 text-center py-1 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 transition-colors"
                        onClick={() => {
                          // Show all requests for this day
                          const firstRequest = dayRequests[3]
                          setSelectedRequest(firstRequest)
                        }}
                      >
                        +{dayRequests.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className={`p-1.5 rounded-lg ${getStatusBgColor(selectedRequest.status)}`}>
                  {React.createElement(getStatusIcon(selectedRequest.status), { 
                    className: `h-4 w-4 ${getStatusTextColor(selectedRequest.status)}` 
                  })}
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Leave Request Details
                  </h3>
                  <p className="text-xs text-gray-500">
                    {selectedRequest.leaveType.name} • {selectedRequest.daysRequested} day{selectedRequest.daysRequested !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedRequest(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XCircleIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Employee Info */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
                  {selectedRequest.employee.firstName[0]}{selectedRequest.employee.lastName[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedRequest.employee.firstName} {selectedRequest.employee.lastName}
                  </p>
                  <p className="text-xs text-gray-600">
                    {selectedRequest.employee.department} • {selectedRequest.employee.position}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Employee #: {selectedRequest.employee.employeeNumber}
                  </p>
                </div>
              </div>

              {/* Leave Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700">Date Range</label>
                    <p className="text-sm text-gray-900 font-semibold mt-0.5">
                      {selectedRequest.startDateObj.toLocaleDateString()} - {selectedRequest.endDateObj.toLocaleDateString()}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-700">Leave Type</label>
                    <p className="text-sm text-gray-900 font-semibold mt-0.5">
                      {selectedRequest.leaveType.name} ({selectedRequest.leaveType.code})
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {selectedRequest.leaveType.isPaid ? 'Paid Leave' : 'Unpaid Leave'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700">Status</label>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold mt-0.5 ${getStatusBgColor(selectedRequest.status)} ${getStatusTextColor(selectedRequest.status)}`}>
                      {React.createElement(getStatusIcon(selectedRequest.status), { className: 'h-3 w-3 mr-1' })}
                      {selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                    </div>
                  </div>
                  
                  {selectedRequest.approver && (
                    <div>
                      <label className="text-xs font-medium text-gray-700">
                        {selectedRequest.status === 'approved' ? 'Approved By' : 'Processed By'}
                      </label>
                      <p className="text-sm text-gray-900 font-semibold mt-0.5">
                        {selectedRequest.approver.firstName} {selectedRequest.approver.lastName}
                      </p>
                      {selectedRequest.approvedAt && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          on {new Date(selectedRequest.approvedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Reason */}
              {selectedRequest.reason && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-2 block">Reason for Leave</label>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-700 leading-relaxed">{selectedRequest.reason}</p>
                  </div>
                </div>
              )}

              {/* Rejection Reason */}
              {selectedRequest.rejectionReason && (
                <div>
                  <label className="text-xs font-medium text-red-700 mb-2 block">Rejection Reason</label>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-xs text-red-700 leading-relaxed">{selectedRequest.rejectionReason}</p>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div>
                <label className="text-xs font-medium text-gray-700 mb-2 block">Timeline</label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-600">Request Submitted</span>
                    <span className="text-xs font-medium text-gray-900">
                      {new Date(selectedRequest.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {selectedRequest.approvedAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Request Processed</span>
                      <span className="text-xs font-medium text-gray-900">
                        {new Date(selectedRequest.approvedAt).toLocaleDateString()}
                    </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setSelectedRequest(null)}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeaveDashboard